import { Download, Share2, Timer, Trash2, Trophy } from "lucide-react";
import type { PublicVideo } from "@pushup/types";
import { resolveApiUrl } from "@/lib/api";

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatDate(epochMs: number): string {
  return new Date(epochMs).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

async function shareVideo(video: PublicVideo): Promise<void> {
  const url = resolveApiUrl(video.mediaUrl);
  if (typeof navigator !== "undefined" && navigator.share) {
    await navigator.share({ title: video.name, url });
    return;
  }
  await navigator.clipboard.writeText(url);
}

export interface PublicVideoCardProps {
  video: PublicVideo;
  /** Called when the owner taps Delete; the caller owns the confirm + mutation. */
  onDelete?: (video: PublicVideo) => void;
  deleting?: boolean;
}

export function PublicVideoCard({ video, onDelete, deleting = false }: PublicVideoCardProps) {
  const handleShare = () => {
    void shareVideo(video).catch(() => undefined);
  };

  const mediaUrl = resolveApiUrl(video.mediaUrl);

  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5">
      <video
        src={mediaUrl}
        controls
        playsInline
        preload="metadata"
        className="aspect-video w-full bg-black object-contain"
      />
      <div className="space-y-1 px-3 py-2.5">
        <p className="truncate text-sm font-semibold text-white">{video.name}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/55">
          {video.celebrityName && (
            <span className="flex items-center gap-1">
              <Trophy className="size-3 text-orange-300" />
              vs {video.celebrityName}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Timer className="size-3" />
            {formatDuration(video.durationSec)}
          </span>
          {video.reps > 0 && <span>{video.reps} reps</span>}
          <span className="ml-auto">{formatDate(video.createdAt)}</span>
        </div>
        <div className="flex gap-2 pt-1">
          <a
            href={mediaUrl}
            download="clip.webm"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-medium text-white/80 transition-colors hover:border-white/30 hover:text-white"
          >
            <Download className="size-3.5" />
            Download
          </a>
          <button
            type="button"
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-medium text-white/80 transition-colors hover:border-white/30 hover:text-white"
          >
            <Share2 className="size-3.5" />
            Share
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(video)}
              disabled={deleting}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-red-400/20 bg-red-400/5 px-2.5 py-1.5 text-xs font-medium text-red-300/90 transition-colors hover:border-red-400/50 hover:text-red-200 disabled:pointer-events-none disabled:opacity-50"
              aria-label={`Delete ${video.name}`}
            >
              <Trash2 className="size-3.5" />
              {deleting ? "Deleting…" : "Delete"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
